import * as THREE from 'three';
import { clamp, smoothstep } from '../utils/math';

interface Palette {
  t: number;
  top: string;
  horizon: string;
  sun: string;
  sunIntensity: number;
  hemiSky: string;
  hemiGround: string;
  hemiIntensity: number;
}

/** Key colours through the day; `t` is the day fraction (0 = midnight). */
const KEYFRAMES: readonly Palette[] = [
  {
    t: 0.0,
    top: '#0d1240',
    horizon: '#2b3270',
    sun: '#8fa6ff',
    sunIntensity: 0.35,
    hemiSky: '#4a5aa8',
    hemiGround: '#1d2a3a',
    hemiIntensity: 0.55,
  },
  {
    t: 0.2,
    top: '#121a52',
    horizon: '#3d3f7e',
    sun: '#8fa6ff',
    sunIntensity: 0.3,
    hemiSky: '#4a5aa8',
    hemiGround: '#1d2a3a',
    hemiIntensity: 0.55,
  },
  {
    t: 0.26,
    top: '#6c8fd6',
    horizon: '#ffb59e',
    sun: '#ffb27a',
    sunIntensity: 1.4,
    hemiSky: '#ffd2c2',
    hemiGround: '#6b7f4a',
    hemiIntensity: 0.9,
  },
  {
    t: 0.34,
    top: '#5cb2ff',
    horizon: '#d4ecff',
    sun: '#fff1d6',
    sunIntensity: 2.6,
    hemiSky: '#d6eeff',
    hemiGround: '#7da55a',
    hemiIntensity: 1.25,
  },
  {
    t: 0.66,
    top: '#5cb2ff',
    horizon: '#d4ecff',
    sun: '#fff1d6',
    sunIntensity: 2.6,
    hemiSky: '#d6eeff',
    hemiGround: '#7da55a',
    hemiIntensity: 1.25,
  },
  {
    t: 0.74,
    top: '#6b6fc7',
    horizon: '#ff9f7f',
    sun: '#ff9a62',
    sunIntensity: 1.4,
    hemiSky: '#ffc2a8',
    hemiGround: '#6b6a4a',
    hemiIntensity: 0.9,
  },
  {
    t: 0.8,
    top: '#121a52',
    horizon: '#3d3f7e',
    sun: '#8fa6ff',
    sunIntensity: 0.3,
    hemiSky: '#4a5aa8',
    hemiGround: '#1d2a3a',
    hemiIntensity: 0.55,
  },
  {
    t: 1.0,
    top: '#0d1240',
    horizon: '#2b3270',
    sun: '#8fa6ff',
    sunIntensity: 0.35,
    hemiSky: '#4a5aa8',
    hemiGround: '#1d2a3a',
    hemiIntensity: 0.55,
  },
];

const RAIN_TOP = new THREE.Color('#7d8aa3');
const RAIN_HORIZON = new THREE.Color('#aab4c4');

const _a = new THREE.Color();
const _b = new THREE.Color();

function samplePalette(dayTime: number) {
  let i = 0;
  while (i < KEYFRAMES.length - 2 && dayTime > (KEYFRAMES[i + 1] as Palette).t) i++;
  const k0 = KEYFRAMES[i] as Palette;
  const k1 = KEYFRAMES[i + 1] as Palette;
  const f = clamp((dayTime - k0.t) / (k1.t - k0.t), 0, 1);
  const mix = (a: string, b: string, out: THREE.Color) => out.set(a).lerp(_b.set(b), f);
  return {
    top: mix(k0.top, k1.top, new THREE.Color()),
    horizon: mix(k0.horizon, k1.horizon, new THREE.Color()),
    sun: mix(k0.sun, k1.sun, new THREE.Color()),
    sunIntensity: k0.sunIntensity + (k1.sunIntensity - k0.sunIntensity) * f,
    hemiSky: mix(k0.hemiSky, k1.hemiSky, new THREE.Color()),
    hemiGround: mix(k0.hemiGround, k1.hemiGround, new THREE.Color()),
    hemiIntensity: k0.hemiIntensity + (k1.hemiIntensity - k0.hemiIntensity) * f,
  };
}

/** 0 at day, 1 at deep night. */
export function nightFactor(dayTime: number): number {
  return 1 - smoothstep(0.2, 0.28, dayTime) + smoothstep(0.73, 0.81, dayTime);
}

const SKY_VERTEX = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 uTop;
  uniform vec3 uHorizon;
  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform float uNight;
  varying vec3 vDir;
  void main() {
    vec3 dir = normalize(vDir);
    float h = dir.y;
    vec3 col = mix(uHorizon, uTop, pow(clamp(h, 0.0, 1.0), 0.55));
    col = mix(col, uHorizon * 0.92, smoothstep(0.0, -0.25, h));
    float sunDot = dot(dir, uSunDir);
    float moonDot = dot(dir, -uSunDir);
    // Sun: soft halo + crisp disc (fades out at night).
    float sun = smoothstep(0.9985, 0.9992, sunDot) + pow(max(sunDot, 0.0), 12.0) * 0.35;
    col += uSunColor * sun * (1.0 - uNight);
    // Moon: pale disc with a subtle glow.
    float moon = smoothstep(0.9990, 0.9994, moonDot) + pow(max(moonDot, 0.0), 40.0) * 0.15;
    col += vec3(0.85, 0.9, 1.0) * moon * uNight;
    gl_FragColor = vec4(col, 1.0);
    #include <colorspace_fragment>
  }
`;

/**
 * Sky dome, sun/moon, stars and the global lighting rig. Everything is driven
 * by the in-game day fraction and the amount of rain.
 */
export class Sky {
  readonly hemi: THREE.HemisphereLight;
  readonly sun: THREE.DirectionalLight;
  readonly fogColor = new THREE.Color();
  /** Normalised direction towards the sun (or moon at night). */
  readonly lightDir = new THREE.Vector3(0, 1, 0);
  night = 0;

  private readonly dome: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  private readonly stars: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private readonly sunDir = new THREE.Vector3();

  constructor(
    private readonly scene: THREE.Scene,
    shadows: boolean,
  ) {
    this.dome = new THREE.Mesh(
      new THREE.SphereGeometry(1500, 32, 16),
      new THREE.ShaderMaterial({
        vertexShader: SKY_VERTEX,
        fragmentShader: SKY_FRAGMENT,
        uniforms: {
          uTop: { value: new THREE.Color() },
          uHorizon: { value: new THREE.Color() },
          uSunDir: { value: new THREE.Vector3(0, 1, 0) },
          uSunColor: { value: new THREE.Color() },
          uNight: { value: 0 },
        },
        side: THREE.BackSide,
        // Drawn first behind everything, so it never needs the depth buffer.
        depthWrite: false,
        depthTest: false,
        fog: false,
      }),
    );
    this.dome.frustumCulled = false;
    this.dome.renderOrder = -10;
    scene.add(this.dome);

    const starCount = 1400;
    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const u = Math.random() * Math.PI * 2;
      const v = Math.acos(Math.random() * 0.95);
      positions[i * 3] = Math.sin(v) * Math.cos(u) * 1400;
      positions[i * 3 + 1] = Math.cos(v) * 1400;
      positions[i * 3 + 2] = Math.sin(v) * Math.sin(u) * 1400;
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.stars = new THREE.Points(
      starGeometry,
      new THREE.PointsMaterial({
        color: '#fffbe8',
        size: 2.2,
        sizeAttenuation: false,
        transparent: true,
        depthWrite: false,
        fog: false,
      }),
    );
    this.stars.frustumCulled = false;
    this.stars.renderOrder = -9;
    scene.add(this.stars);

    this.hemi = new THREE.HemisphereLight('#d6eeff', '#7da55a', 1.2);
    scene.add(this.hemi);

    this.sun = new THREE.DirectionalLight('#fff1d6', 2.5);
    this.sun.castShadow = shadows;
    if (shadows) {
      this.sun.shadow.mapSize.set(2048, 2048);
      const cam = this.sun.shadow.camera;
      cam.left = -32;
      cam.right = 32;
      cam.top = 32;
      cam.bottom = -32;
      cam.near = 1;
      cam.far = 200;
      this.sun.shadow.bias = -0.0006;
      this.sun.shadow.normalBias = 0.03;
    }
    scene.add(this.sun);
    scene.add(this.sun.target);

    scene.fog = new THREE.Fog('#d4ecff', 90, 1000);
  }

  update(dayTime: number, rain: number, camera: THREE.Camera, focus: THREE.Vector3): void {
    const palette = samplePalette(dayTime);
    this.night = nightFactor(dayTime);

    if (rain > 0) {
      palette.top.lerp(_a.copy(RAIN_TOP).multiplyScalar(1 - this.night * 0.7), rain * 0.85);
      palette.horizon.lerp(_a.copy(RAIN_HORIZON).multiplyScalar(1 - this.night * 0.7), rain * 0.85);
      palette.sunIntensity *= 1 - rain * 0.55;
    }

    // Sun travels east (+x) → overhead → west, slightly to the south so the
    // farm (seen from the south) is nicely front-lit.
    const angle = (dayTime - 0.25) * Math.PI * 2;
    this.sunDir.set(Math.cos(angle), Math.sin(angle), 0.35).normalize();
    // Light comes from the sun by day and the moon by night.
    this.lightDir.copy(this.sunDir.y > -0.05 ? this.sunDir : _negate(this.sunDir));
    if (this.lightDir.y < 0.25) this.lightDir.y = 0.25;
    this.lightDir.normalize();

    const uniforms = this.dome.material.uniforms;
    (uniforms.uTop!.value as THREE.Color).copy(palette.top);
    (uniforms.uHorizon!.value as THREE.Color).copy(palette.horizon);
    (uniforms.uSunDir!.value as THREE.Vector3).copy(this.sunDir);
    (uniforms.uSunColor!.value as THREE.Color).copy(palette.sun);
    uniforms.uNight!.value = this.night;

    this.dome.position.copy(camera.position);
    this.stars.position.copy(camera.position);
    this.stars.rotation.y = dayTime * Math.PI * 2 * 0.25;
    this.stars.material.opacity = this.night * (1 - rain * 0.9);
    this.stars.visible = this.stars.material.opacity > 0.01;

    this.hemi.color.copy(palette.hemiSky);
    this.hemi.groundColor.copy(palette.hemiGround);
    this.hemi.intensity = palette.hemiIntensity * (1 - rain * 0.2);

    this.sun.color.copy(palette.sun);
    this.sun.intensity = palette.sunIntensity;
    this.sun.position.copy(focus).addScaledVector(this.lightDir, 80);
    this.sun.target.position.copy(focus);

    this.fogColor.copy(palette.horizon);
    const fog = this.scene.fog as THREE.Fog;
    fog.color.copy(this.fogColor);
    fog.far = 1000 - rain * 450;
  }
}

const _neg = new THREE.Vector3();
function _negate(v: THREE.Vector3): THREE.Vector3 {
  return _neg.copy(v).negate();
}
